import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getUserThreads } from '../../../services/firestore/messages';
import { getBooking } from '../../../services/firestore/bookings';
import { getUserData } from '../../../services/auth';
import { useAuth } from '../../../contexts/AuthContext';
import { MessageSquare, ArrowRight, Calendar, MapPin, Clock } from 'lucide-react';

const Messages = () => {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      if (!user) return;
      try {
        const threadsData = await getUserThreads(user.uid).catch(() => []);
        
        // Enrich threads with booking and participant info
        const enrichedThreads = await Promise.all(
          threadsData
            .filter(thread => thread.bookingId) // Only show threads with bookingId
            .map(async (thread) => {
              let booking = null;
              let otherParticipant = null;
              
              // Get booking info
              if (thread.bookingId) {
                try {
                  booking = await getBooking(thread.bookingId);
                } catch (error) {
                  console.error('Error loading booking:', error);
                }
              }
              
              // Get other participant info (client)
              const otherParticipantId = thread.participants?.find(id => id !== user.uid);
              if (otherParticipantId) {
                try {
                  otherParticipant = await getUserData(otherParticipantId);
                } catch (error) {
                  console.error('Error loading participant:', error);
                }
              }
              
              return {
                ...thread,
                booking,
                otherParticipant,
              };
            })
        );
        
        setThreads(enrichedThreads);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Messages</h1>
          <p className="text-lg text-gray-600">
            All your conversations with guests
          </p>
        </div>

        {threads.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">No messages yet</h3>
            <p className="text-gray-600 mb-6">
              Conversations will appear here once guests start messaging you about bookings.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {threads.map((thread) => {
              const lastMessage = thread.lastMessage;
              const lastMessageDate = lastMessage?.timestamp?.toDate 
                ? lastMessage.timestamp.toDate() 
                : lastMessage?.timestamp 
                ? new Date(lastMessage.timestamp) 
                : null;
              
              const booking = thread.booking;
              const otherParticipant = thread.otherParticipant;
              const navigateTo = `/host/bookings/${thread.bookingId}?tab=messages`;

              return (
                <Link
                  key={thread.id}
                  to={navigateTo}
                  className="block group bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Header with participant and booking info */}
                      <div className="flex items-start gap-4 mb-3">
                        {/* Participant Avatar */}
                        {otherParticipant?.photoURL ? (
                          <img
                            src={otherParticipant.photoURL}
                            alt={otherParticipant.displayName || 'Guest'}
                            className="w-12 h-12 rounded-full flex-shrink-0"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-600 to-green-700 flex items-center justify-center text-white font-medium flex-shrink-0">
                            {(otherParticipant?.displayName || otherParticipant?.email || 'G')[0].toUpperCase()}
                          </div>
                        )}
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-semibold text-gray-900 truncate">
                              {otherParticipant?.displayName || otherParticipant?.email || 'Guest'}
                            </h3>
                            {booking && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                {booking.type === 'inquiry' ? 'Inquiry' : 'Booking'}
                              </span>
                            )}
                          </div>
                          
                          {/* Booking Info */}
                          {booking && (
                            <div className="space-y-1 mb-2">
                              {booking.tripTitle && (
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {booking.tripTitle}
                                </p>
                              )}
                              {booking.tripLocation && (
                                <div className="flex items-center gap-2 text-gray-600">
                                  <MapPin className="w-3 h-3 text-emerald-600 flex-shrink-0" />
                                  <span className="text-xs truncate">{booking.tripLocation}</span>
                                </div>
                              )}
                              {booking.selectedDate && (
                                <div className="flex items-center gap-2 text-gray-600">
                                  <Calendar className="w-3 h-3 text-emerald-600 flex-shrink-0" />
                                  <span className="text-xs">
                                    {(() => {
                                      const date = booking.selectedDate?.toDate 
                                        ? booking.selectedDate.toDate() 
                                        : new Date(booking.selectedDate);
                                      return date.toLocaleDateString('en-US', { 
                                        month: 'short', 
                                        day: 'numeric', 
                                        year: 'numeric' 
                                      });
                                    })()}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Last Message Preview */}
                          {lastMessage && (
                            <div className="mt-2">
                              <p className="text-sm text-gray-600 line-clamp-2">
                                <span className="font-medium">{lastMessage.senderName}:</span>{' '}
                                {lastMessage.content}
                              </p>
                              {lastMessageDate && (
                                <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                                  <Clock className="w-3 h-3" />
                                  <span>
                                    {lastMessageDate.toLocaleDateString('en-US', { 
                                      month: 'short', 
                                      day: 'numeric' 
                                    })} at {lastMessageDate.toLocaleTimeString('en-US', { 
                                      hour: 'numeric', 
                                      minute: '2-digit' 
                                    })}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 text-emerald-700 group-hover:text-emerald-800 transition-colors flex-shrink-0">
                      <span className="text-sm font-medium">View</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Messages;
